import { Alert, Button, PasswordInput, Progress, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { MIN_PASSWORD_LENGTH } from '@mexp/shared';
import { errorMessage } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';

function strength(password: string): number {
  let score = 0;
  if (password.length >= MIN_PASSWORD_LENGTH) score += 30;
  if (password.length >= 12) score += 20;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 20;
  if (/\d/.test(password)) score += 15;
  if (/[^A-Za-z0-9]/.test(password)) score += 15;
  return Math.min(score, 100);
}

export function ChangePasswordForm({ currentLabel = 'Current password', onDone }: { currentLabel?: string; onDone?: () => void }) {
  const { changePassword } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const form = useForm({
    initialValues: { currentPassword: '', newPassword: '', confirm: '' },
    validate: {
      currentPassword: (v) => (v ? null : 'Required'),
      newPassword: (v, values) =>
        v.length < MIN_PASSWORD_LENGTH
          ? `Use at least ${MIN_PASSWORD_LENGTH} characters`
          : v === values.currentPassword
            ? 'Choose a password different from the current one'
            : null,
      confirm: (v, values) => (v === values.newPassword ? null : 'The passwords do not match'),
    },
  });
  const score = strength(form.values.newPassword);

  const submit = form.onSubmit(async (values) => {
    setSaving(true);
    setError(null);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      form.reset();
      onDone?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  });

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="md">
        {error && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
            {error}
          </Alert>
        )}
        <PasswordInput label={currentLabel} autoComplete="current-password" {...form.getInputProps('currentPassword')} />
        <Stack gap={6}>
          <PasswordInput label="New password" autoComplete="new-password" {...form.getInputProps('newPassword')} />
          {form.values.newPassword && (
            <>
              <Progress value={score} size={4} color={score < 50 ? 'red' : score < 80 ? 'yellow' : 'teal'} aria-label="Password strength" />
              <Text fz="xs" c="dimmed">
                Longer is stronger. Mixing upper and lower case, numbers and symbols helps.
              </Text>
            </>
          )}
        </Stack>
        <PasswordInput label="Repeat new password" autoComplete="new-password" {...form.getInputProps('confirm')} />
        <Button type="submit" loading={saving}>
          Update password
        </Button>
      </Stack>
    </form>
  );
}
